import type { FlightMoneySource } from '../results/flight_offer_source.js';
import type { FlightCabinPolicy } from '../results/filters/cabin_policy.js';
import type { FlightExplorerOffer } from './search_source.js';
import {
  composeRecommended,
  filterAndSortOffers,
  filterOffers,
  type FlightFilterState,
  type SortKey,
} from '../results/filter_logic.js';
import type { FlightExplorerRequest, FlightPageInfo, FlightSearchParams } from './search_source.js';
import type { FlightSearchFailureCause, LegFailure } from './search_source.js';
import { editedRequest, type ExplorerTripGeometry } from './explorer_request.js';
import type { FlightFilterIntent, FlightFilterPatch } from '../results/filter_intent_source.js';
import type { ChipsContext, FilterChip } from '../results/filter_chips_state.js';
import { CHIP_HANDLERS } from './explorer_chip_handlers.js';
import { OUTBOUND_HANDLERS } from './explorer_outbound_handlers.js';
import { ceilingOfLeg, legFailureOf, mergeOffers, offersOf } from './explorer_legs.js';
import { filterAfterRefresh, resetFilter } from './explorer_filter_reset.js';
import {
  emptyLandingRollback,
  restoreLeg,
  restoreScene,
  sceneOf,
  type ExplorerScene,
} from './explorer_cabin_rollback.js';

export {
  editedRequest,
  legSearchParams,
  sameTripGeometry,
  tripGeometryOf,
  type ExplorerTripGeometry,
} from './explorer_request.js';
export { explorerConfirm, faresLegOf, isFaresStage, isFinalPick } from './explorer_confirm.js';

/**
 * Estado puro do FlightExplorer (zero React) — a MESMA máquina nos três hosts:
 * drawer do `flight_offers` (one_way/round_trip), CTA por trecho do
 * `flight_multi_offers` (one_way de UMA perna, rotulado `{kind:'leg'}` pela
 * casca) e a bancada do web-chat (one_way/RT/multi_leg).
 *
 * Round-trip em 2 fases: fase 1 SELECIONA o voo da IDA (sem escada — as tarifas
 * reais são as da matriz do par, que só existe na fase 2), o return-search da
 * MESMA sessão traz os pares na perna 1, fase 2 abre a escada do par no ref de
 * VOLTA. Multi-leg de N pernas: fan-out de UMA busca
 * one-way POR PERNA no mount, stepper de N passos, pick por perna, confirm
 * agregado `{kind:'multi'}`. `seqs` é o guarda anti-stale do padrão rental
 * (search_drawer.tsx), POR PERNA: resposta cujo seq não é o corrente da SUA
 * perna é descartada. `confirmed` congela o estado inteiro — single-fire é lei
 * do estado, não só do botão.
 */

/**
 * `seeded` é a lista que o CARD já tinha quando o explorer abriu, com a busca viva
 * em voo por cima dela. Não é `ready`: a semente vem capada
 * (`CHAT_UI_MAX_ALL_OFFERS`, 100 de um inventário que passa de 500 numa rota
 * internacional) e sai de uma sessão de busca do AGENTE, que pode ter morrido — e
 * é a âncora do `searchKey` que o filtro server-side usa. Tratá-la como resposta
 * final era o que fazia o explorer semeado nunca re-buscar: refine na sessão morta
 * falha calado (`useServerFilter` degrada pro corte no cliente) e a coluna voltava
 * a contar só a semente (então 30) de 544, exatamente o bug que o filtro
 * server-side existe pra matar.
 *
 * Semente e busca não competem: a semente é o primeiro quadro (0ms, sem esqueleto)
 * e a busca é a autoridade que pousa por cima.
 */
export type ExplorerLegPhase =
  | { phase: 'idle' }
  | {
      phase: 'loading';
      /** Re-busca por cima de uma lista em cena (#3213): o quadro que volta se a
       *  busca falhar — lista e os campos do pedido que a re-busca reescreveu. */
      fallback?: LegFallback;
    }
  | { phase: 'seeded'; offers: FlightExplorerOffer[] }
  | {
      phase: 'ready';
      offers: FlightExplorerOffer[];
      /** Cursor do scroll infinito — presente quando o servidor paginou. */
      page?: FlightPageInfo;
      /** Página seguinte em voo (sentinela ocupada). */
      loadingMore?: boolean;
      /** Teto de preço da BUSCA (facetas do refine). Depois de um refine as
       *  ofertas são um RECORTE — o teto delas não é o do conjunto, e voltar a
       *  esta perna zerando o filtro por ele cunhava um corte de preço (#3844). */
      ceiling?: number;
    }
  // `failure` dá à página de erro um desfecho próprio: `timeout` é o prazo
  // estourado no servidor (#4465) — na cidade inteira oferece os aeroportos
  // dela; `policy_evaluation` é a política do viajante que não avaliou (#5376) —
  // terminal, sem "tentar de novo".
  | { phase: 'error'; failure?: LegFailure };

interface LegFallback {
  offers: FlightExplorerOffer[];
  departDate: string;
  cabin: FlightExplorerRequest['cabin'];
  /** O corte da perna ATIVA na hora do disparo — o disparo o zera
   *  (`defaultFilterState([])`, teto 0) e, sem devolvê-lo, a lista restaurada
   *  voltava inteira cortada. Perna de fundo não mexe no filtro: sem campo. */
  filter?: FlightFilterState;
}

/** Ids opacos da escada — o MESMO shape que o `select_offer_fares` espalha. */
export type ExplorerFares =
  | { bundledFareId: string }
  | { outboundFareId: string; returnFareId?: string };

/**
 * O que o painel mostrou na hora do pick — vira o texto do dispatch no host.
 *
 * Preço é o único campo OBRIGATÓRIO, e a razão é que nem todo pick passa pelo
 * painel: o card `flight_multi_offers` semeia no explorer os trechos que ele já
 * fechou (`initialChosen`), e para esses o painel não mostrou nada — o card guarda
 * oferta, tarifa e preço, nunca o NOME da tarifa. Os três campos de exibição só
 * são lidos pelo `FarePickChip`, que é gated em `mode !== 'multi_leg'`; preencher
 * com filler o que o roteiro não tem seria uma mentira que um dia aparece num chip.
 */
interface ExplorerPickSummary {
  position?: number;
  carrier?: string;
  fareNames?: string;
  total: number;
  currency: string;
}

/**
 * Escolha de UMA perna.
 *
 * Na fase 1 do RT vem SÓ `offerId` — nem tarifa, nem `summary`: a fase 1 escolhe
 * o VOO (as tarifas reais são as da matriz do par, que só existe na fase 2), e o
 * `summary` é o único insumo do parcial — gravá-lo aqui somaria a ida por fora
 * do total do par.
 */
export interface ExplorerPick {
  offerId: string;
  fares?: ExplorerFares;
  summary?: ExplorerPickSummary;
}

/**
 * O payload do "Confirmar seleção". O reducer produz `single` (one_way/RT) e
 * `multi` (N pernas); `leg` é o rótulo POR PERNA que a casca
 * (`FlightExplorerDialog` com `legIndex`) aplica sobre um `single` — o CTA por
 * trecho do card multi.
 */
export type ExplorerConfirm =
  | {
      kind: 'single';
      offerId: string;
      fares: ExplorerFares;
      summary: ExplorerPickSummary;
      /** A viagem que produziu o pick (o `state.request` do momento do confirm).
       *  É contra ela que o host decide o galho: igual à de abertura ⇒ o caminho
       *  de hoje; divergente ⇒ dispatch imediato. */
      trip: ExplorerTripGeometry;
    }
  | {
      kind: 'leg';
      legIndex: number;
      offerId: string;
      fareId: string;
      summary: ExplorerPickSummary;
    }
  | {
      kind: 'multi';
      legs: { legIndex: number; offerId: string; fareId: string; price: FlightMoneySource }[];
      total: FlightMoneySource;
      trip: ExplorerTripGeometry;
    };

export interface ExplorerState {
  /** O pedido corrente — a faixa de busca re-escreve rota e datas (e liga/desliga
   *  a volta) por cima dele, e a cabine vem da coluna de cortes. `request.mode`
   *  é O modo do explorer. */
  request: FlightExplorerRequest;
  /** As cabines que a política do viajante permite, do último pouso que a trouxe
   *  (#6438). É do viajante, não da lista: busca nova sem ela não a apaga. */
  cabinPolicy?: FlightCabinPolicy;
  activeLeg: number;
  /** Fase POR PERNA (RT: [ida, volta]; multi: uma por trecho do request) —
   *  generaliza o `offersByLeg` do design §4 com loading/error explícitos. */
  legs: ExplorerLegPhase[];
  /**
   * A SEMENTE por perna — a lista que o host já tinha (o trio do card) no momento
   * da abertura. Imutável: é o quadro de abertura DESTA viagem, não a lista viva.
   * Serve para desenhar o pick do host quando a oferta dele não está na lista viva
   * (as refs são por sessão, então nunca estão) e para casar a linha do mesmo voo
   * por identidade (`liveOfferForPick`). `[]` na perna sem semente; RT sempre `[]`.
   *
   * Uma viagem REFORMADA (`search_started`, `legs_edited`) não tem semente — o
   * quadro de abertura era de outro roteiro — por isso os dois handlers zeram
   * `seeds` para uma lista vazia por perna nova, do mesmo tamanho do roteiro.
   */
  seeds: readonly FlightExplorerOffer[][];
  /** Escolha posicional ESPARSA por perna (RT: {0: ida}, {1: volta+tarifa};
   *  multi: {i: pick do trecho i}). */
  chosen: Partial<Record<number, ExplorerPick>>;
  /** Estado dos eixos de corte — o host o traduz em `refine` no servidor quando o
   *  seam tem a pista, e corta na página em mão quando não. Reset por lista ATIVA. */
  filter: FlightFilterState;
  /** Chips do filtro por texto (spec flight-text-filter-chips): patch + undo por eixo. Vivem
   *  com o filtro da perna ativa — todo reset de filtro os zera. */
  chips: FilterChip[];
  /** Sequência POR PERNA da última busca disparada — resposta com outro seq é
   *  stale. Nunca encolhe (flip one_way↔RT preserva a cauda: o espelho de IO do
   *  componente não pode dessincronizar). É também a GERAÇÃO da busca que o filtro
   *  server-side lê pra saber de quem são as facetas e a âncora do `refine`: só
   *  disparo de busca avança este número. */
  seqs: number[];
  /**
   * QUAL perna está com a lista ordenada pelo provider (resposta de refine).
   * Enquanto ela é a ativa, a ordem do array é a autoridade e o cliente só corta:
   * reordenar localmente ordenaria a PÁGINA, que é um subconjunto — perderia a
   * ordem do CONJUNTO que o provider devolveu.
   *
   * É POR PERNA, não um booleano global: com um booleano, uma perna NÃO-ATIVA
   * pousando (fan-out do multi) apagava a marca da perna em cena e a lista curada
   * voltava a ser reordenada por preço no cliente, sem gesto de ninguém.
   */
  serverOrderedLeg: number | null;
  confirmed: boolean;
  /**
   * O pick FINAL fechou a viagem, mas as pernas vieram em moedas diferentes —
   * `explorerConfirm` se calou por construção (`sumMoney`) e a escada ficou
   * aberta de propósito (`takePickFor`) porque não há mais CTA de header como
   * rede de segurança. Vivia num `useState` do componente até o CodeRabbit
   * #2174 apontar que só um pick NOVO o zerava — remover o trecho travado
   * (`pick_removed`) ou começar outra busca deixavam o aviso aceso para uma
   * seleção que já não existe. Movido pro reducer: limpa em qualquer evento
   * que reabre a perna ou reforma a seleção (`pick_removed`, `fares_picked`,
   * `search_started`, `legs_edited`, `leg_search_started`) — e também em
   * `search_ok` quando o pouso da busca viva PODA o pick corrente
   * (`pickAfterRefresh`, rodada 3 do CodeRabbit #2174): a busca sobre uma
   * semente pode derrubar exatamente a oferta que o aviso travou.
   */
  confirmStuck: boolean;
  /**
   * Picks se PERDERAM neste explorer — poda (a oferta do pick saiu da lista
   * viva) ou re-forma da faixa (`search_started`/`legs_edited`/`leg_search_started`
   * limpam picks). Hoje `chosen: {}` não registra a CAUSA, e no confirm não há
   * como saber se escolhas se perderam (gap pinado na spec, rodada 8 da emenda
   * #1510) — o aviso divergente da ShellBar usa a frase completa ("descarta as
   * escolhas dos outros trechos") só com isto ligado. One-way de propósito:
   * pick re-feito não des-descarta o que o card tinha. `pick_removed` NÃO liga
   * — é gesto explícito da pessoa, não poda.
   */
  picksDiscarded: boolean;
  /**
   * A re-busca de UMA perna falhou e a perna VOLTOU ao quadro anterior (#3213):
   * lista, pick e cabine do pedido restaurados. `params` são os da tentativa —
   * é o que o "Tentar de novo" do aviso re-dispara (o pedido já não os tem).
   * Morre em qualquer disparo de busca. `failure` é a causa com desfecho próprio
   * (#5376: a política que não avaliou tira o "Tentar de novo" do aviso). `empty`
   * é a re-busca que POUSOU sem oferta (#6438): não há voo na cabine pedida, e o
   * aviso diz isso em vez de oferecer repetir a mesma busca vazia.
   */
  legRollback: {
    leg: number;
    params: FlightSearchParams;
    failure?: LegFailure;
    empty?: true;
  } | null;
  /**
   * A troca de cabine no one_way/RT em voo (#6438): a cena que ela refaz, guardada
   * para voltar se a busca nova falhar ou pousar vazia (`restoreScene`). Morre no
   * pouso com ofertas e em qualquer outro disparo de busca. Ausente = desligado.
   */
  sceneFallback?: ExplorerScene | null;
  /** Cabine pedida por TEXTO, à espera do seam (R2-D4). O componente consome e zera. */
  cabinRequest: NonNullable<FlightFilterPatch['cabin']> | null;
  /**
   * A busca vencida foi refeita e a ida escolhida não voltou nela (#5588, CT-6):
   * a fase 1 mostra as idas novas com o aviso. Morre quando a pessoa escolhe outra
   * ida ou dispara outra busca. Ausente = desligado.
   */
  outboundGone?: boolean;
}

export type ExplorerEvent =
  /** Faixa numa aba BINÁRIA: re-busca INTEIRA (params podem ligar/desligar a volta,
   *  e podem SAIR de um roteiro multi — a aba é autoritativa sobre a forma).
   *  `keepScene`: é a cabine da coluna, a MESMA viagem — a cena atual fica guardada
   *  para a falha ou o pouso vazio (#6438). */
  | { t: 'search_started'; params: FlightSearchParams; keepScene?: boolean }
  /** Faixa na aba MULTI: o roteiro inteiro foi re-editado (rota, datas, nº de
   *  trechos). Re-forma o pedido e re-busca todas as pernas. */
  | { t: 'legs_edited'; legs: FlightExplorerRequest['legs'] }
  /** UMA perna: fan-out do mount e re-busca da COLUNA de cortes no multi. */
  | { t: 'leg_search_started'; leg: number; params: FlightSearchParams }
  /** A busca viva saiu por cima de uma perna SEMEADA. Irmão do
   *  `leg_search_started` e diferente dele no que importa: aquele troca a perna
   *  por `loading` (pedido NOVO — outra data, outra cabine), este preserva a
   *  lista e o filtro, porque é o MESMO pedido que a semente já
   *  responde. Só o seq avança, e é ele que autoriza o pouso. */
  | { t: 'refresh_started'; leg: number }
  | {
      t: 'search_ok';
      leg: number;
      seq: number;
      offers: FlightExplorerOffer[];
      page?: FlightPageInfo;
      cabinPolicy?: FlightCabinPolicy | undefined;
    }
  /** Página do FILTRO server-side: a mesma busca, refiltrada no provider.
   *  `append` = página >1 do scroll — concatena em vez de substituir. */
  | {
      t: 'refine_ok';
      leg: number;
      offers: FlightExplorerOffer[];
      page?: FlightPageInfo;
      append?: boolean;
      /** Teto de preço do CONJUNTO, das facetas desta resposta. */
      ceiling?: number;
    }
  /** Página seguinte pedida (scroll) — liga o loadingMore da perna. */
  | { t: 'page_started'; leg: number }
  /** Página seguinte falhou — a lista fica, a sentinela volta a oferecer retry. */
  | { t: 'page_failed'; leg: number }
  | { t: 'search_failed'; leg: number; seq: number; cause?: FlightSearchFailureCause }
  /** Fase 1 do RT: o VOO da ida escolhido (a linha é select puro — tarifa é
   *  assunto da fase 2, onde a matriz do par carrega os preços reais). */
  | { t: 'outbound_chosen'; offerId: string }
  /** A busca refeita sobre uma busca vencida (#5588) não trouxe a ida escolhida:
   *  a pessoa fica na fase 1, com as idas novas e o aviso. */
  | { t: 'outbound_gone' }
  /** Fase 2 do RT: o PAR escolhido (a linha também é select puro desde a #2262 —
   *  a escada saiu da lista e virou a tela de tarifas, a fase 3). */
  | { t: 'return_chosen'; offerId: string }
  /** one_way/multi: o VOO da perna (select puro, #3875); `fares_picked` completa o pick. */
  | { t: 'flight_chosen'; leg: number; offerId: string }
  | { t: 'go_to_leg'; index: number }
  | { t: 'fares_picked'; leg: number; pick: ExplorerPick }
  /** O ✕ do chip do passo (RT e multi): derruba o pick e volta à fase da perna. */
  | { t: 'pick_removed'; leg: number }
  | { t: 'filter_changed'; filter: FlightFilterState }
  | { t: 'filter_reset'; filter: FlightFilterState }
  | { t: 'chip_pending'; id: string; text: string }
  | { t: 'chip_resolved'; id: string; intent: FlightFilterIntent; ctx: ChipsContext }
  | { t: 'chip_failed'; id: string }
  | { t: 'chip_removed'; id: string }
  | { t: 'sort_changed'; sort: SortKey }
  /** O componente consumiu `cabinRequest` — o reducer o zera ANTES de o componente agir
   *  (R2-D4): um re-render não pode disparar duas buscas pro mesmo pedido. */
  | { t: 'cabin_request_consumed' }
  | { t: 'confirmed' }
  /** O degelo do voltar-do-checkout: o ÚNICO evento que um explorer congelado
   *  aceita. Derruba o `confirmed` preservando listas, picks e filtro — a lista
   *  volta utilizável exatamente como ficou (voltar do checkout da VOLTA de um
   *  RT não pode zerar a ida). Quem re-arma o single-fire do componente é o
   *  espelho do `resumeSeq` em `explorer.tsx`. */
  | { t: 'unconfirmed' }
  /** O pick final fechou a viagem, mas as pernas divergiram de moeda —
   *  `takePickFor` manda este evento no lugar de chamar um callback do
   *  componente (CodeRabbit #2174). Só dispara com `confirmed` ainda false. */
  | { t: 'confirm_unavailable' };

const withoutPick = (chosen: ExplorerState['chosen'], leg: number): ExplorerState['chosen'] =>
  Object.fromEntries(Object.entries(chosen).filter(([k]) => Number(k) !== leg));

const hasPicks = (chosen: ExplorerState['chosen']): boolean =>
  Object.values(chosen).some((pick) => pick !== undefined);

/**
 * O pick da perna sobrevive ao pouso da busca?
 *
 * - Pouso sobre a SEMENTE (`refresh_started`, a busca de mount que a pessoa não
 *   pediu): SEMPRE sobrevive. A lista viva é de outra sessão do modal-session e
 *   nenhum `offerId` bate — nem o do mesmo voo —, então podar por id apagava
 *   toda escolha do chat ao abrir a bancada. A ref do pick (sessão do agente)
 *   segue válida e adotável; o chip desenha pela semente (`state.seeds`).
 * - Pouso de re-busca PEDIDA (`leg_search_started`, qualquer `loading` — com ou
 *   sem `fallback`: o campo só carrega a lista de resgate para a falha, e uma
 *   perna sem oferta em cena ainda assim é um `loading` de pedido reformado):
 *   poda se a oferta saiu — re-buscar o trecho é pedir outra coisa, e um chip
 *   apontando para um voo fora de cena mentiria.
 *
 * A origem do pouso é o `was.phase`, não a presença de `fallback` — uma perna
 * que pousou vazia (`search_ok` com `offers: []`) segue `loading` sem
 * `fallback` (nada para resgatar), e uma re-busca pedida sobre ela também
 * precisa podar.
 */
function pickAfterRefresh(
  state: ExplorerState,
  leg: number,
  was: ExplorerLegPhase | undefined,
  offers: FlightExplorerOffer[],
): ExplorerState['chosen'] {
  const picked = state.chosen[leg]?.offerId;
  if (picked === undefined || was?.phase === 'seeded') return state.chosen;
  return offers.some((o) => o.offerId === picked) ? state.chosen : withoutPick(state.chosen, leg);
}

export function initialExplorerState(init: {
  request: FlightExplorerRequest;
  /**
   * Semente POR PERNA (índice = perna): a lista que o HOST já tinha. one_way passa
   * `[allOffers]` do card; o roteiro passa o trio de cada trecho. Perna sem entrada
   * nasce `idle` e o mount busca só ela.
   *
   * RT NUNCA semeia, e não é conservadorismo: as ofertas do card RT são PARES, e
   * o ref de um par é de VOLTA. A fase 1 do explorer escolhe a IDA por um ref
   * one-way de ida — é dele que sai o `returnSearch`. Semear a fase 1 com ref de
   * par mandaria o return-search atrás das voltas de uma "ida" que já é o par.
   */
  seedOffers?: FlightExplorerOffer[][];
  /** Onde o stepper ABRE — a perna que o host estava mostrando quando a pessoa
   *  pediu a lista completa. Fora da faixa cai na 0: índice torto não pode abrir
   *  um passo que não existe. */
  initialLeg?: number;
  /** Trechos que o HOST já fechou (o Board do card multi). Entram como picks de
   *  verdade: o stepper abre com eles resolvidos e o confirm agregado só espera o
   *  que falta — senão abrir o explorer custaria refazer o roteiro inteiro. */
  initialChosen?: Partial<Record<number, ExplorerPick>>;
  /** Poda PRÉ-mount: o host filtrou picks cujas ofertas saíram da lista ANTES de
   *  montar (`explorerPicksOf` no card multi) — o reducer nunca vê esses, então
   *  o descarte inicial entra como fato consumado. */
  initialPicksDiscarded?: boolean;
}): ExplorerState {
  const { request } = init;
  const seeds = request.mode === 'round_trip' ? undefined : init.seedOffers;
  const legs: ExplorerLegPhase[] =
    request.mode === 'round_trip'
      ? [{ phase: 'idle' }, { phase: 'idle' }]
      : request.legs.map((_, i) => {
          const seed = seeds?.[i];
          return seed && seed.length > 0 ? { phase: 'seeded', offers: seed } : { phase: 'idle' };
        });
  const wanted = init.initialLeg ?? 0;
  const activeLeg = Number.isInteger(wanted) && wanted >= 0 && wanted < legs.length ? wanted : 0;
  // Filtro sai da perna ATIVA, não da 0: abrir no trecho 2 com o teto de
  // preço do trecho 0 seria um corte que ninguém deu.
  const active = offersOf(legs[activeLeg]);
  return {
    request,
    activeLeg,
    legs,
    seeds: legs.map((_, i) => seeds?.[i] ?? []),
    chosen: { ...init.initialChosen },
    ...resetFilter(active),
    serverOrderedLeg: null,
    seqs: legs.map(() => 0),
    confirmed: false,
    confirmStuck: false,
    picksDiscarded: init.initialPicksDiscarded ?? false,
    legRollback: null,
    cabinRequest: null,
  };
}

type Handler<K extends ExplorerEvent['t']> = (
  state: ExplorerState,
  ev: Extract<ExplorerEvent, { t: K }>,
) => ExplorerState;

/** Uma função por evento (em vez de um switch) — mantém cada transição sob o
 *  teto de complexidade e testável pelo reducer público. */
const HANDLERS: { [K in ExplorerEvent['t']]: Handler<K> } = {
  search_started: (state, ev) => {
    // Os params da faixa REESCREVEM o pedido — inclusive o modo, que segue o
    // returnDate. Vale SAINDO de um roteiro multi: quem apertou `Buscar` numa aba
    // binária pediu uma viagem de uma perna, e recusar a re-forma aqui deixava a
    // busca reescrevendo a perna ATIVA com o stepper de N passos ainda em cena —
    // um roteiro que ninguém pediu mais.
    const mode = ev.params.returnDate ? ('round_trip' as const) : ('one_way' as const);
    const request: FlightExplorerRequest = {
      mode,
      legs: [
        {
          origin: ev.params.origin,
          destination: ev.params.destination,
          departDate: ev.params.departureDate,
        },
      ],
      ...(ev.params.returnDate ? { returnDate: ev.params.returnDate } : {}),
      ...(ev.params.cabin ? { cabin: ev.params.cabin } : {}),
      ...(ev.params.policyTravelerId ? { policyTravelerId: ev.params.policyTravelerId } : {}),
      // Sobrevive à re-forma: quem viaja não muda porque a rota mudou (e a faixa
      // de busca sequer tem eixo de passageiros pra reescrevê-lo).
      ...(state.request.travelerCount ? { travelerCount: state.request.travelerCount } : {}),
      // Idem a linhagem do card multi: a rota mudou, o card de origem não.
      ...(state.request.itineraryChallengeId
        ? { itineraryChallengeId: state.request.itineraryChallengeId }
        : {}),
    };
    const seqs = [...state.seqs];
    seqs[0] = (seqs[0] ?? 0) + 1;
    return {
      ...state,
      request,
      activeLeg: 0,
      chosen: {},
      legs:
        mode === 'round_trip' ? [{ phase: 'loading' }, { phase: 'idle' }] : [{ phase: 'loading' }],
      seqs,
      seeds: mode === 'round_trip' ? [[], []] : [[]],
      ...resetFilter([]),
      picksDiscarded: state.picksDiscarded || hasPicks(state.chosen),
      confirmStuck: false,
      legRollback: null,
      outboundGone: false,
      sceneFallback: ev.keepScene ? sceneOf(state) : null,
    };
  },
  /**
   * O roteiro é OUTRO pedido, não um filtro sobre o anterior: picks, listas e
   * filtro morrem juntos e TODAS as pernas re-buscam. Re-buscar só a perna mexida
   * deixaria as outras exibindo ofertas de uma sessão de busca que já não descreve
   * a viagem — e um pick preservado de lá seria recusado na adoção (a validação
   * server-side casa rota+data da oferta com o trecho do roteiro).
   */
  legs_edited: (state, ev) => {
    const seqs = [...state.seqs];
    // Cada perna estreia um seq (a nova inclusive, via `?? 0`); a cauda de um
    // roteiro que ENCOLHEU fica onde está — `seqs` nunca encolhe, senão o espelho
    // de IO do componente dessincroniza e um pouso atrasado passaria por corrente.
    ev.legs.forEach((_, i) => {
      seqs[i] = (seqs[i] ?? 0) + 1;
    });
    return {
      ...state,
      request: editedRequest(state.request, ev.legs),
      activeLeg: 0,
      chosen: {},
      legs: ev.legs.map(() => ({ phase: 'loading' as const })),
      seqs,
      seeds: ev.legs.map(() => []),
      ...resetFilter([]),
      picksDiscarded: state.picksDiscarded || hasPicks(state.chosen),
      confirmStuck: false,
      legRollback: null,
      outboundGone: false,
      sceneFallback: null,
    };
  },
  /**
   * A perna que JÁ TINHA lista (semente do chat ou busca pousada) não a perde no
   * disparo, nem o pick (#3213): o quadro vai pro `fallback` e só o pouso decide —
   * `search_ok` poda o pick se a oferta saiu, `search_failed` restaura tudo. Antes
   * a troca de cabine descartava a escolha do trecho ANTES de saber se a busca nova
   * daria certo, e a falha deixava "Tentar de novo" sem caminho de volta.
   */
  leg_search_started: (state, ev) => {
    const was = state.legs[ev.leg];
    const prevLeg = state.request.legs[ev.leg];
    if (!was || !prevLeg) return state;
    const offers = offersOf(was);
    const legs = [...state.legs];
    const active = ev.leg === state.activeLeg;
    legs[ev.leg] =
      offers.length > 0
        ? {
            phase: 'loading',
            fallback: {
              offers,
              departDate: prevLeg.departDate,
              cabin: state.request.cabin,
              ...(active ? { filter: state.filter } : {}),
            },
          }
        : { phase: 'loading' };
    const seqs = [...state.seqs];
    seqs[ev.leg] = (seqs[ev.leg] ?? 0) + 1;
    return {
      ...state,
      // Data/cabine da re-busca valem pro pedido (rota da perna é fixa).
      // Cabine: ausência VENCE. Um spread condicional deixaria a cabine velha
      // filtrando as re-buscas seguintes (`legSearchParams` lê `request.cabin`)
      // sempre que a re-busca não nomeasse cabine — e o campo é opcional no
      // evento. `search_started` não sofre disso: reconstrói o request inteiro.
      request: {
        ...state.request,
        legs: state.request.legs.map((l, i) =>
          i === ev.leg ? { ...l, departDate: ev.params.departureDate } : l,
        ),
        cabin: ev.params.cabin,
      },
      legs,
      seqs,
      confirmStuck: false,
      legRollback: null,
      sceneFallback: null,
      ...(active ? resetFilter([]) : {}),
    };
  },
  search_ok: (state, ev) => {
    // `seeded` entra junto de `loading`: é uma perna com busca em voo, só que com
    // lista em cena. Sem isto o pouso da re-busca por cima da semente era dropado
    // em silêncio e a semente virava permanente.
    const was = state.legs[ev.leg];
    const awaiting = was?.phase === 'loading' || was?.phase === 'seeded';
    if (ev.seq !== state.seqs[ev.leg] || !awaiting) return state;
    if (ev.cabinPolicy) state = { ...state, cabinPolicy: ev.cabinPolicy };
    // A re-busca da coluna que pousou VAZIA não apaga o que estava em cena (#6438):
    // volta o quadro anterior com o aviso de que não há voo na cabine pedida.
    const kept = emptyLandingRollback(state, ev.leg, was, ev.offers);
    if (kept) return kept;
    if (ev.leg === 0) state = { ...state, sceneFallback: null };
    const legs = [...state.legs];
    legs[ev.leg] = { phase: 'ready', offers: ev.offers, ...(ev.page ? { page: ev.page } : {}) };
    const chosen = pickAfterRefresh(state, ev.leg, was, ev.offers);
    // Poda do pouso da re-busca PEDIDA: `pickAfterRefresh` devolve o MESMO objeto
    // quando nada caiu — a identidade é o detector, aqui e no aviso abaixo.
    const pruned = chosen !== state.chosen;
    state = {
      ...state,
      chosen,
      picksDiscarded: state.picksDiscarded || pruned,
      // CodeRabbit #2174 (rodada 3): a poda acima pode derrubar EXATAMENTE o pick
      // que o `confirm_unavailable` (moedas mistas) tinha travado — sem isto o
      // aviso sobrevivia apontando pra uma seleção que já não existe. Pick
      // preservado não mexe no aviso (só quem reabre a perna ou reforma a
      // seleção o limpa, ver a doc de `confirmStuck`).
      confirmStuck: state.confirmStuck && !pruned,
    };
    // A perna que POUSOU uma busca perde a marca (a lista é nova); as outras não.
    state = state.serverOrderedLeg === ev.leg ? { ...state, serverOrderedLeg: null } : state;
    // Filtro acompanha a lista ATIVA — pouso de perna não-ativa não o toca.
    if (ev.leg !== state.activeLeg) return { ...state, legs };
    return {
      ...state,
      legs,
      ...(was.phase === 'seeded'
        ? { filter: filterAfterRefresh(state.filter, was.offers, ev.offers) }
        : resetFilter(ev.offers, undefined, { chips: state.chips, leg: ev.leg })),
    };
  },
  /**
   * A página que o filtro server-side devolveu. NÃO é `search_ok`, e as duas
   * diferenças são o motivo do evento existir: ① a perna está `ready` (o guarda de
   * `loading` do `search_ok` derrubava a página em silêncio — o filtro chamava o
   * servidor e a lista não mudava); ② o corte NÃO é redefinido, senão o clique que
   * disparou o refine morria junto com a resposta dele.
   *
   * Sem seq próprio: quem guarda contra resposta stale é o hook — a última chamada
   * ganha, e ele invalida o que está em voo quando a BUSCA troca (`seqs[leg]` avança;
   * um refine não avança nada, por isso este evento não mexe em `seqs`). O
   * `!== 'ready'` abaixo cobre só a janela em que a busca nova está em voo: sozinho
   * ele não impediria uma página refiltrada de pousar DEPOIS que a lista nova chegou.
   */
  refine_ok: (state, ev) => {
    const was = state.legs[ev.leg];
    if (was?.phase !== 'ready') return state;
    const legs = [...state.legs];
    if (ev.append) {
      // Página >1 do scroll: concatena, atualiza o cursor e apaga o loadingMore.
      // NÃO mexe em filtro nem em serverOrderedLeg — carregar mais não é um corte
      // nem muda quem manda na ordem.
      const merged = mergeOffers(was.offers, ev.offers);
      // A primeira página pode vir sem facetas e o teto chegar só numa seguinte.
      const ceiling = ev.ceiling ?? was.ceiling;
      legs[ev.leg] = {
        phase: 'ready',
        offers: merged,
        ...(ev.page ? { page: ev.page } : was.page ? { page: was.page } : {}),
        ...(ceiling !== undefined ? { ceiling } : {}),
      };
      // A exceção é a lista que estava VAZIA: esta página é o primeiro quadro
      // dela, e o filtro nasce dela como no `search_ok`. O `defaultFilterState([])`
      // da lista vazia deixa `maxPrice: 0`, e sem isto a página concatenada saía
      // TODA cortada por um teto que ninguém escolheu — "Nenhum voo com esses
      // filtros" sobre voos que acabaram de chegar (a fase 2 do RT quando o
      // servidor devolvia a página 1 vazia com `hasNext`).
      const firstFrame = was.offers.length === 0 && ev.leg === state.activeLeg;
      return {
        ...state,
        legs,
        ...(firstFrame ? resetFilter(merged, undefined, { chips: state.chips, leg: ev.leg }) : {}),
      };
    }
    legs[ev.leg] = {
      phase: 'ready',
      offers: ev.offers,
      ...(ev.page ? { page: ev.page } : {}),
      ...(ev.ceiling !== undefined ? { ceiling: ev.ceiling } : {}),
    };
    if (ev.leg !== state.activeLeg) return { ...state, legs };
    return {
      ...state,
      legs,
      serverOrderedLeg: ev.leg,
    };
  },
  /** Página seguinte pedida (sentinela do scroll) — só liga o spinner de
   *  rodapé; a lista não muda até `refine_ok`/`page_failed` responder. */
  page_started: (state, ev) => {
    const was = state.legs[ev.leg];
    if (was?.phase !== 'ready') return state;
    const legs = [...state.legs];
    legs[ev.leg] = { ...was, loadingMore: true };
    return { ...state, legs };
  },
  /** Página seguinte falhou — desliga o spinner e deixa a lista como estava,
   *  pra sentinela oferecer retry sem perder o que já carregou. */
  page_failed: (state, ev) => {
    const was = state.legs[ev.leg];
    if (was?.phase !== 'ready') return state;
    const legs = [...state.legs];
    legs[ev.leg] = { ...was, loadingMore: false };
    return { ...state, legs };
  },
  search_failed: (state, ev) => {
    const was = state.legs[ev.leg];
    const awaiting = was?.phase === 'loading' || was?.phase === 'seeded';
    if (ev.seq !== state.seqs[ev.leg] || !awaiting) return state;
    const legs = [...state.legs];
    // A re-busca que falhou sobre uma SEMENTE deixa a semente — e como `ready`, que
    // é o fim da linha dela. Trocar duas ofertas legítimas por um alerta com
    // "Tentar de novo" seria regressão dupla: perde a lista E perde o degrade que o
    // one-way semeado já entregava antes de existir re-busca nenhuma.
    if (was.phase === 'seeded') {
      legs[ev.leg] = { phase: 'ready', offers: was.offers };
      return { ...state, legs };
    }
    // Idem para a re-busca por cima de uma lista em cena — a da cabine no
    // one_way/RT (#6438) e a de perna do multi (#3213): volta o quadro guardado,
    // reverte no pedido o que a re-busca reescreveu e deixa o aviso recuperável
    // com os params da tentativa.
    if (ev.leg === 0 && state.sceneFallback)
      return restoreScene(state, state.sceneFallback, legFailureOf(ev.cause));
    if (was.fallback) return restoreLeg(state, ev.leg, was.fallback, legFailureOf(ev.cause));
    legs[ev.leg] = { phase: 'error', ...legFailureOf(ev.cause) };
    return { ...state, legs };
  },
  refresh_started: (state, ev) => {
    if (state.legs[ev.leg]?.phase !== 'seeded') return state;
    const seqs = [...state.seqs];
    seqs[ev.leg] = (seqs[ev.leg] ?? 0) + 1;
    return { ...state, seqs };
  },
  ...OUTBOUND_HANDLERS,
  /**
   * O par escolhido na fase 2. Grava o pick SEM tarifa — de propósito, e é o
   * mesmo formato do `outbound_chosen`: a tarifa é escolhida na fase 3
   * (`isFaresStage`), e é ela que completa o pick com `fares`+`summary` via
   * `fares_picked`. Sem esse degrau intermediário a tela de tarifas não teria
   * como existir: `explorerConfirm` fecharia (ou não) no mesmo gesto do clique
   * na lista.
   */
  return_chosen: (state, ev) => {
    if (state.request.mode !== 'round_trip' || state.chosen[0] === undefined) return state;
    if (state.legs[1]?.phase !== 'ready' && state.legs[1]?.phase !== 'seeded') return state;
    return { ...state, chosen: { ...state.chosen, 1: { offerId: ev.offerId } }, activeLeg: 1 };
  },
  flight_chosen: (state, ev) => {
    if (state.request.mode === 'round_trip' || offersOf(state.legs[ev.leg]).length === 0)
      return state;
    const chosen = { ...state.chosen, [ev.leg]: { offerId: ev.offerId } };
    return { ...state, chosen, activeLeg: ev.leg, confirmStuck: false };
  },
  go_to_leg: (state, ev) => {
    const leg = state.legs[ev.index];
    if (!leg || leg.phase === 'idle' || ev.index === state.activeLeg) return state;
    return {
      ...state,
      activeLeg: ev.index,
      ...resetFilter(offersOf(leg), ceilingOfLeg(leg), { chips: state.chips, leg: ev.index }),
    };
  },
  fares_picked: (state, ev) => {
    // Zera a cada NOVO pick: só o gesto corrente decide se o aviso de moeda
    // mista segue aceso (o próprio pick pode ser quem reacende via
    // `confirm_unavailable`, despachado DEPOIS deste evento por `takePickFor`).
    const chosen = { ...state.chosen, [ev.leg]: ev.pick };
    if (state.request.mode !== 'multi_leg') return { ...state, chosen, confirmStuck: false };
    // Multi: avança pro primeiro trecho pendente (fica onde está se não houver).
    const pending = state.legs.findIndex((_, i) => i !== ev.leg && chosen[i] === undefined);
    if (pending < 0) return { ...state, chosen, confirmStuck: false };
    const target = state.legs[pending];
    return {
      ...state,
      chosen,
      activeLeg: pending,
      ...resetFilter(offersOf(target), ceilingOfLeg(target), { chips: state.chips, leg: pending }),
      confirmStuck: false,
    };
  },
  pick_removed: (state, ev) => {
    if (state.chosen[ev.leg] === undefined) return state;
    const target = state.legs[ev.leg];
    // Round-trip: tirar a IDA derruba a volta JUNTO, pick e lista. As ofertas da
    // perna 1 são os PARES daquela ida (o return-search saiu do ref dela) — deixar
    // qualquer uma das duas coisas de pé faria a fase 2 seguir precificando um par
    // cuja ida ninguém escolheu mais. Tirar a VOLTA (perna 1) só reabre a fase 2:
    // a ida segue valendo e os pares dela continuam servindo.
    const droppingOutbound = state.request.mode === 'round_trip' && ev.leg === 0;
    // "Voltar à fase da perna": o pick sai e a perna reabre como ativa. O aviso
    // de moeda mista some junto — a seleção que ele descrevia não existe mais.
    return {
      ...state,
      chosen: droppingOutbound ? {} : withoutPick(state.chosen, ev.leg),
      ...(droppingOutbound
        ? { legs: [target ?? { phase: 'idle' }, { phase: 'idle' } as const] }
        : {}),
      activeLeg: ev.leg,
      ...resetFilter(offersOf(target), ceilingOfLeg(target), { chips: state.chips, leg: ev.leg }),
      confirmStuck: false,
    };
  },
  ...CHIP_HANDLERS,
  confirmed: (state) => ({ ...state, confirmed: true }),
  unconfirmed: (state) => ({ ...state, confirmed: false }),
  confirm_unavailable: (state) => ({ ...state, confirmStuck: true }),
};

export function reduceExplorer(state: ExplorerState, ev: ExplorerEvent): ExplorerState {
  if (state.confirmed && ev.t !== 'unconfirmed') return state;
  return (HANDLERS[ev.t] as (s: ExplorerState, e: ExplorerEvent) => ExplorerState)(state, ev);
}

/**
 * A busca da perna ATIVA está em voo (ou nem saiu ainda). UMA conta, dois
 * consumidores: o corpo, que troca a lista pelo esqueleto, e a COLUNA de cortes,
 * que troca os eixos pelo esqueleto deles — os eixos saem das ofertas, então
 * enquanto elas não chegam não há eixo a derivar.
 *
 * `idle` conta como espera porque é o estado pré-IO da montagem; congelado ele é
 * terminal, e quem trata isso é cada consumidor (ver `ExplorerBody`).
 */
export function searchingLeg(state: ExplorerState): boolean {
  const phase = state.legs[state.activeLeg]?.phase;
  return phase === 'loading' || phase === 'idle';
}

/** Ofertas (cruas) da perna ativa — alimenta painel, teto de política e eixos. */
export function activeOffers(state: ExplorerState): FlightExplorerOffer[] {
  return offersOf(state.legs[state.activeLeg]);
}

/** Lista exibida = filtro + ordenação corrente. A ordem do provider, quando há
 *  marca da perna, manda sobre a local: ela é do CONJUNTO, a local é da página.
 *  Em "Recomendado" (o default) a lista final é a COMPOSIÇÃO do #2108: os
 *  destacados pelos badges na frente, o resto na ordem que manda. */
export function visibleOffers(state: ExplorerState): FlightExplorerOffer[] {
  const offers = activeOffers(state);
  // Ordem do provider manda: só cortamos o que ele não filtra (chegada, aeroporto).
  const base =
    state.serverOrderedLeg === state.activeLeg
      ? filterOffers(offers, state.filter)
      : filterAndSortOffers(offers, state.filter);
  return state.filter.sort === 'rec' ? composeRecommended(base) : base;
}

/** Alguma busca em voo SEM lista em cena? (congela a faixa de busca e a cabine da
 *  coluna). `seeded` fica de fora de propósito: ali há lista utilizável, e
 *  congelar a faixa por uma busca que a pessoa nem pediu tiraria dela o único
 *  caminho de corrigir a rota enquanto o primeiro quadro está em tela. */
export function explorerBusy(state: ExplorerState): boolean {
  return state.legs.some((l) => l.phase === 'loading');
}

/** A perna ATIVA está mostrando a semente do chat com a busca viva em voo. Marca
 *  a lista como "atualizando" — e ao contrário do refine NÃO a inertiza: a
 *  semente é clicável, senão o primeiro quadro não serviria para nada. */
export function explorerRefreshing(state: ExplorerState): boolean {
  return state.legs[state.activeLeg]?.phase === 'seeded';
}
